/**
 * ESocialEventDetailsModal Component
 * Modal for viewing eSocial event details and logs
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Grid,
  Divider,
  Chip,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Collapse,
  IconButton
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  KeyboardArrowDown,
  KeyboardArrowRight
} from '@mui/icons-material';
import {
  ESocialEvent,
  ESocialEventLog,
  ESOCIAL_EVENT_TYPE_LABELS,
  ESOCIAL_STATUS_LABELS,
  ESOCIAL_LOG_ACTION_LABELS,
  getLogActionColor
} from '../../../../../types/esocial';
import { esocialService } from '../../../../../services/esocialService';
import ESocialEventStatusChip from '../ESocialEventStatusChip';
import XmlPreviewPanel from '../XmlPreviewPanel';

interface ESocialEventDetailsModalProps {
  open: boolean;
  onClose: () => void;
  event: ESocialEvent | null;
  companyId: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

function hasTransmissionDetails(log: ESocialEventLog): boolean {
  return !!(log.responseCode || log.responseMessage || log.responseXml);
}

function getResponseCodeColor(code: string | undefined): 'success' | 'error' | 'warning' | 'default' {
  if (!code) return 'default';
  if (code === '201') return 'success';
  if (code.startsWith('2')) return 'warning';
  return 'error';
}

function formatXml(xml: string): string {
  let formatted = '';
  let indent = 0;
  const tokens = xml.replace(/>\s*</g, '><').split(/(<[^>]+>)/);
  for (const token of tokens) {
    if (!token.trim()) continue;
    if (token.startsWith('</')) {
      indent = Math.max(0, indent - 1);
      formatted += '  '.repeat(indent) + token + '\n';
    } else if (token.startsWith('<') && !token.startsWith('<?') && !token.endsWith('/>') && !token.includes('</')) {
      formatted += '  '.repeat(indent) + token + '\n';
      indent++;
    } else {
      formatted += '  '.repeat(indent) + token + '\n';
    }
  }
  return formatted.trim();
}

const ESocialEventDetailsModal: React.FC<ESocialEventDetailsModalProps> = ({
  open,
  onClose,
  event,
  companyId
}) => {
  const [tabValue, setTabValue] = useState(0);
  const [logs, setLogs] = useState<ESocialEventLog[]>([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsError, setLogsError] = useState<string | null>(null);
  const [expandedLogId, setExpandedLogId] = useState<number | null>(null);

  const toggleLogExpand = (logId: number) => {
    setExpandedLogId((prev) => (prev === logId ? null : logId));
  };

  useEffect(() => {
    if (open && event) {
      loadLogs();
      setTabValue(0);
    }
  }, [open, event?.id]);

  const loadLogs = async () => {
    if (!event) return;

    setLogsLoading(true);
    setLogsError(null);
    try {
      const data = await esocialService.getEventLogs(companyId, event.id.toString());
      setLogs(data);
    } catch (err: unknown) {
      setLogsError(err instanceof Error ? err.message : 'Erro ao carregar histórico');
    } finally {
      setLogsLoading(false);
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  const formatDateTime = (dateString?: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('pt-BR');
  };

  if (!event) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            Detalhes do Evento
          </Typography>
          <ESocialEventStatusChip status={event.eventStatus} />
        </Box>
      </DialogTitle>
      <DialogContent>
        <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)}>
          <Tab label="Informações" />
          <Tab label="Histórico" />
          <Tab label="XML" />
        </Tabs>

        {/* Informacoes Tab */}
        <TabPanel value={tabValue} index={0}>
          <Grid container spacing={2}>
            <Grid
              size={{
                xs: 12,
                sm: 6
              }}>
              <Typography variant="subtitle2" color="text.secondary">
                Tipo de Evento
              </Typography>
              <Typography variant="body1">
                {ESOCIAL_EVENT_TYPE_LABELS[event.eventType]}
              </Typography>
            </Grid>

            <Grid
              size={{
                xs: 12,
                sm: 6
              }}>
              <Typography variant="subtitle2" color="text.secondary">
                Status
              </Typography>
              <Typography variant="body1">
                {ESOCIAL_STATUS_LABELS[event.eventStatus]}
              </Typography>
            </Grid>

            <Grid
              size={{
                xs: 12,
                sm: 6
              }}>
              <Typography variant="subtitle2" color="text.secondary">
                Data do Evento
              </Typography>
              <Typography variant="body1">
                {formatDate(event.eventDate)}
              </Typography>
            </Grid>

            <Grid
              size={{
                xs: 12,
                sm: 6
              }}>
              <Typography variant="subtitle2" color="text.secondary">
                Data Limite
              </Typography>
              <Typography variant="body1">
                {formatDate(event.dueDate)}
              </Typography>
            </Grid>

            {event.employee && (
              <Grid size={12}>
                <Typography variant="subtitle2" color="text.secondary">
                  Funcionário
                </Typography>
                <Typography variant="body1">
                  {event.employee.name}
                  {event.employee.cpf && ` (CPF: ${event.employee.cpf})`}
                </Typography>
              </Grid>
            )}

            <Grid size={12}>
              <Divider sx={{ my: 1 }} />
            </Grid>

            <Grid
              size={{
                xs: 12,
                sm: 6
              }}>
              <Typography variant="subtitle2" color="text.secondary">
                Número do Recibo
              </Typography>
              <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                {event.receiptNumber ?? '-'}
              </Typography>
            </Grid>

            <Grid
              size={{
                xs: 12,
                sm: 6
              }}>
              <Typography variant="subtitle2" color="text.secondary">
                Número do Protocolo
              </Typography>
              <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                {event.protocolNumber ?? '-'}
              </Typography>
            </Grid>

            <Grid
              size={{
                xs: 12,
                sm: 6
              }}>
              <Typography variant="subtitle2" color="text.secondary">
                Data de Transmissao
              </Typography>
              <Typography variant="body1">
                {formatDateTime(event.submissionDate)}
              </Typography>
            </Grid>

            <Grid
              size={{
                xs: 12,
                sm: 6
              }}>
              <Typography variant="subtitle2" color="text.secondary">
                Data da Resposta
              </Typography>
              <Typography variant="body1">
                {formatDateTime(event.responseDate)}
              </Typography>
            </Grid>

            {event.errorCode && (
              <>
                <Grid size={12}>
                  <Divider sx={{ my: 1 }} />
                </Grid>
                <Grid size={12}>
                  <Alert severity="error">
                    <Typography variant="subtitle2">
                      Erro: {event.errorCode}
                    </Typography>
                    <Typography variant="body2">
                      {event.errorMessage}
                    </Typography>
                  </Alert>
                </Grid>
              </>
            )}

            {event.notes && (
              <>
                <Grid size={12}>
                  <Divider sx={{ my: 1 }} />
                </Grid>
                <Grid size={12}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Observacoes
                  </Typography>
                  <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                    {event.notes}
                  </Typography>
                </Grid>
              </>
            )}

            <Grid size={12}>
              <Divider sx={{ my: 1 }} />
            </Grid>

            <Grid
              size={{
                xs: 12,
                sm: 6
              }}>
              <Typography variant="subtitle2" color="text.secondary">
                Criado em
              </Typography>
              <Typography variant="body1">
                {formatDateTime(event.createdAt)}
              </Typography>
            </Grid>

            <Grid
              size={{
                xs: 12,
                sm: 6
              }}>
              <Typography variant="subtitle2" color="text.secondary">
                Criado por
              </Typography>
              <Typography variant="body1">
                {event.creator?.name || '-'}
              </Typography>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Historico Tab */}
        <TabPanel value={tabValue} index={1}>
          {logsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : logsError ? (
            <Alert severity="error">{logsError}</Alert>
          ) : logs.length === 0 ? (
            <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
              Nenhum registro de histórico encontrado
            </Typography>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 40 }} />
                    <TableCell>Data/Hora</TableCell>
                    <TableCell>Ação</TableCell>
                    <TableCell>Status Anterior</TableCell>
                    <TableCell>Novo Status</TableCell>
                    <TableCell>Usuario</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {logs.map((log) => {
                    const hasTxDetails = hasTransmissionDetails(log);
                    const isExpanded = expandedLogId === log.id;

                    return (
                      <React.Fragment key={log.id}>
                        <TableRow
                          hover={hasTxDetails}
                          onClick={() => hasTxDetails && toggleLogExpand(log.id)}
                          sx={{ cursor: hasTxDetails ? 'pointer' : 'default', '& > *': { borderBottom: isExpanded ? 'unset' : undefined } }}
                        >
                          <TableCell sx={{ width: 40, p: 0.5 }}>
                            {hasTxDetails && (
                              <IconButton size="small">
                                {isExpanded ? <KeyboardArrowDown /> : <KeyboardArrowRight />}
                              </IconButton>
                            )}
                          </TableCell>
                          <TableCell>
                            {formatDateTime(log.createdAt)}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={ESOCIAL_LOG_ACTION_LABELS[log.action]}
                              color={getLogActionColor(log.action)}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            {log.previousStatus
                              ? ESOCIAL_STATUS_LABELS[log.previousStatus as keyof typeof ESOCIAL_STATUS_LABELS] || log.previousStatus
                              : '-'}
                          </TableCell>
                          <TableCell>
                            {log.newStatus
                              ? ESOCIAL_STATUS_LABELS[log.newStatus as keyof typeof ESOCIAL_STATUS_LABELS] || log.newStatus
                              : '-'}
                          </TableCell>
                          <TableCell>
                            {log.user?.name || '-'}
                          </TableCell>
                        </TableRow>

                        {hasTxDetails && (
                          <TableRow>
                            <TableCell colSpan={6} sx={{ py: 0, px: 2 }}>
                              <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                <Box sx={{ py: 2 }}>
                                  {log.responseCode && (
                                    <Box sx={{ mb: 1 }}>
                                      <Chip
                                        label={`cdResposta: ${log.responseCode}`}
                                        color={getResponseCodeColor(log.responseCode)}
                                        size="small"
                                      />
                                    </Box>
                                  )}

                                  {log.responseMessage && (
                                    <Box
                                      component="pre"
                                      sx={{
                                        backgroundColor: 'action.hover',
                                        p: 1.5,
                                        borderRadius: 1,
                                        fontSize: '0.8rem',
                                        fontFamily: 'monospace',
                                        whiteSpace: 'pre-wrap',
                                        wordBreak: 'break-word',
                                        maxHeight: 200,
                                        overflow: 'auto',
                                        mb: 1,
                                      }}
                                    >
                                      {log.responseMessage}
                                    </Box>
                                  )}

                                  {log.responseXml && (
                                    <Accordion
                                      disableGutters
                                      sx={{ border: '1px solid', borderColor: 'divider', '&:before': { display: 'none' }, '&.Mui-expanded': { margin: 0 } }}
                                    >
                                      <AccordionSummary
                                        expandIcon={<ExpandMoreIcon />}
                                        sx={{ flexDirection: 'row-reverse', gap: 1, minHeight: 40, '&.Mui-expanded': { minHeight: 40 } }}
                                      >
                                        <Typography variant="body2">XML de Resposta</Typography>
                                      </AccordionSummary>
                                      <AccordionDetails sx={{ p: 0 }}>
                                        <Box
                                          component="pre"
                                          sx={{
                                            backgroundColor: 'action.hover',
                                            p: 2,
                                            borderRadius: 0,
                                            overflowY: 'auto',
                                            maxHeight: 300,
                                            fontSize: '0.75rem',
                                            fontFamily: 'monospace',
                                            whiteSpace: 'pre-wrap',
                                            wordBreak: 'break-all',
                                            m: 0,
                                          }}
                                        >
                                          {formatXml(log.responseXml)}
                                        </Box>
                                      </AccordionDetails>
                                    </Accordion>
                                  )}
                                </Box>
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </TabPanel>

        {/* XML Tab */}
          <TabPanel value={tabValue} index={2}>
            {/* XML Preview (generate on demand) */}
            <XmlPreviewPanel companyId={companyId} eventId={event.id.toString()} />

            {event.xmlContent && (
              <>
              <Divider sx={{ my: 2 }} />
              <Accordion
                defaultExpanded
                disableGutters
                sx={{ border: '1px solid', borderColor: 'divider', '&:before': { display: 'none' }, '&.Mui-expanded': { margin: 0 } }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{ flexDirection: 'row-reverse', gap: 1, minHeight: 40, '&.Mui-expanded': { minHeight: 40 } }}
                >
                  <Typography>XML do Evento</Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 0 }}>
                  <Box
                    component="pre"
                    sx={{
                      backgroundColor: 'action.hover',
                      p: 2,
                      borderRadius: 0,
                      overflowY: 'auto',
                      maxHeight: 300,
                      fontSize: '0.75rem',
                      fontFamily: 'monospace',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-all',
                      m: 0,
                    }}
                  >
                    {formatXml(event.xmlContent)}
                  </Box>
                </AccordionDetails>
              </Accordion>
              </>
            )}

            {event.responseXml && (
              <Accordion
                disableGutters
                sx={{ border: '1px solid', borderColor: 'divider', mt: 1, '&:before': { display: 'none' }, '&.Mui-expanded': { margin: 0, mt: 1 } }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{ flexDirection: 'row-reverse', gap: 1, minHeight: 40, '&.Mui-expanded': { minHeight: 40 } }}
                >
                  <Typography>XML de Resposta</Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 0 }}>
                  <Box
                    component="pre"
                    sx={{
                      backgroundColor: 'action.hover',
                      p: 2,
                      borderRadius: 0,
                      overflowY: 'auto',
                      maxHeight: 300,
                      fontSize: '0.75rem',
                      fontFamily: 'monospace',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-all',
                      m: 0,
                    }}
                  >
                    {formatXml(event.responseXml)}
                  </Box>
                </AccordionDetails>
              </Accordion>
            )}
          </TabPanel>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>
          Fechar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ESocialEventDetailsModal;
